import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Search,
  MoreHorizontal,
  ShieldCheck,
  UserPlus,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import BillingSidebar from "@/components/layout/BillingSidebar";
import BillingHeader from "@/components/layout/BillingHeader";
import ProviderDetailSheet from "@/components/compliance/ProviderDetailSheet";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

const statusColors: Record<string, string> = {
  active: "bg-emerald-100 text-emerald-700",
  pending_onboarding: "bg-blue-100 text-blue-700",
  pending_verification: "bg-amber-100 text-amber-700",
  expired_license: "bg-red-100 text-red-700",
  suspended: "bg-red-100 text-red-700",
  inactive: "bg-muted text-muted-foreground",
  not_enrolled: "bg-muted text-muted-foreground",
};

const statusLabels: Record<string, string> = {
  active: "Active",
  pending_onboarding: "Pending Onboarding",
  pending_verification: "Pending Verification",
  expired_license: "Expired License",
  suspended: "Suspended",
  inactive: "Inactive",
  not_enrolled: "Not Enrolled",
};

export default function ProviderDatabase() {
  const [searchQuery, setSearchQuery] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [enrollDialogOpen, setEnrollDialogOpen] = useState(false);
  const [enrollProviderId, setEnrollProviderId] = useState<string | null>(null);
  const [selectedComplianceId, setSelectedComplianceId] = useState<string | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const queryClient = useQueryClient();

  const [enrollForm, setEnrollForm] = useState({
    npi_number: "",
    tax_id: "",
    specialty: "",
    primary_state: "",
    notes: "",
  });

  const { data, isLoading } = useQuery({
    queryKey: ["provider-database"],
    queryFn: async () => {
      const [providersRes, complianceRes, licensesRes] = await Promise.all([
        supabase.from("providers").select("id, display_name, specialty, user_id"),
        supabase.from("provider_compliance").select("*"),
        supabase.from("provider_licenses").select("*"),
      ]);

      return {
        providers: providersRes.data || [],
        compliance: complianceRes.data || [],
        licenses: licensesRes.data || [],
      };
    },
  });

  const enrollMutation = useMutation({
    mutationFn: async ({ providerId, form }: { providerId: string; form: typeof enrollForm }) => {
      const { error } = await supabase.from("provider_compliance").insert({
        provider_id: providerId,
        npi_number: form.npi_number || null,
        tax_id: form.tax_id || null,
        specialty: form.specialty || null,
        primary_state: form.primary_state || null,
        notes: form.notes || null,
        overall_status: "pending_onboarding",
      });
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Provider enrolled in compliance tracking");
      setEnrollDialogOpen(false);
      setEnrollProviderId(null);
      setEnrollForm({ npi_number: "", tax_id: "", specialty: "", primary_state: "", notes: "" });
      queryClient.invalidateQueries({ queryKey: ["provider-database"] });
      queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    },
    onError: (error: any) => {
      toast.error(error.message || "Failed to enroll provider");
    },
  });

  const updateStatusMutation = useMutation({
    mutationFn: async ({ id, status }: { id: string; status: string }) => {
      const { error } = await supabase
        .from("provider_compliance")
        .update({ overall_status: status })
        .eq("id", id);
      if (error) throw error;
    },
    onSuccess: () => {
      toast.success("Status updated");
      queryClient.invalidateQueries({ queryKey: ["provider-database"] });
      queryClient.invalidateQueries({ queryKey: ["compliance-dashboard"] });
    },
  });

  const providers = data?.providers || [];
  const compliance = data?.compliance || [];
  const licenses = data?.licenses || [];

  const getCompliance = (providerId: string) =>
    compliance.find((c) => c.provider_id === providerId) || null;

  const getLicenseCount = (complianceId: string) =>
    licenses.filter((l) => l.compliance_id === complianceId).length;

  const getStatus = (providerId: string) => {
    const comp = getCompliance(providerId);
    return comp ? comp.overall_status : "not_enrolled";
  };

  const filtered = providers.filter((p) => {
    const name = (p.display_name || "").toLowerCase();
    const comp = getCompliance(p.id);
    const npi = comp?.npi_number || "";
    const matchesSearch =
      name.includes(searchQuery.toLowerCase()) || npi.includes(searchQuery);
    const status = getStatus(p.id);
    const matchesStatus = statusFilter === "all" || status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const openEnrollDialog = (providerId: string) => {
    const provider = providers.find((p) => p.id === providerId);
    setEnrollProviderId(providerId);
    setEnrollForm({
      npi_number: "",
      tax_id: "",
      specialty: provider?.specialty || "",
      primary_state: "",
      notes: "",
    });
    setEnrollDialogOpen(true);
  };

  return (
    <div className="flex min-h-screen bg-background">
      <BillingSidebar />
      <div className="flex-1 flex flex-col">
        <BillingHeader />
        <main className="flex-1 p-6 overflow-auto">
          <div className="max-w-7xl mx-auto space-y-6">
            <div>
              <h1 className="text-3xl font-bold text-foreground">Provider Database</h1>
              <p className="text-muted-foreground">
                Manage provider profiles, credentials, and compliance records
              </p>
            </div>

            {/* Filters */}
            <Card>
              <CardContent className="p-4">
                <div className="flex gap-4">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                    <Input
                      placeholder="Search by name or NPI..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      className="pl-10"
                    />
                  </div>
                  <Select value={statusFilter} onValueChange={setStatusFilter}>
                    <SelectTrigger className="w-48">
                      <SelectValue placeholder="All Statuses" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">All Statuses</SelectItem>
                      {Object.entries(statusLabels).map(([k, v]) => (
                        <SelectItem key={k} value={k}>
                          {v}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </CardContent>
            </Card>

            {/* Provider Table */}
            <Card>
              <CardHeader>
                <CardTitle>Providers ({filtered.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {isLoading ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
                  </div>
                ) : filtered.length === 0 ? (
                  <div className="text-center py-12 text-muted-foreground">
                    <ShieldCheck className="h-12 w-12 mx-auto mb-3 opacity-50" />
                    <p className="font-medium">No providers found</p>
                    <p className="text-sm">Try adjusting your search or filters</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead>Provider</TableHead>
                        <TableHead>NPI</TableHead>
                        <TableHead>Specialty</TableHead>
                        <TableHead>Primary State</TableHead>
                        <TableHead>Licenses</TableHead>
                        <TableHead>Status</TableHead>
                        <TableHead className="w-12"></TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((provider) => {
                        const comp = getCompliance(provider.id);
                        const status = comp ? comp.overall_status : "not_enrolled";

                        return (
                          <TableRow
                            key={provider.id}
                            className={comp ? "cursor-pointer" : ""}
                            onClick={() => {
                              if (comp) {
                                setSelectedComplianceId(comp.id);
                                setDetailOpen(true);
                              }
                            }}
                          >
                            <TableCell className="font-medium">
                              {provider.display_name}
                            </TableCell>
                            <TableCell className="font-mono text-sm">
                              {comp?.npi_number || "—"}
                            </TableCell>
                            <TableCell>{comp?.specialty || provider.specialty || "—"}</TableCell>
                            <TableCell>{comp?.primary_state || "—"}</TableCell>
                            <TableCell>
                              <Badge variant="outline">
                                {comp ? getLicenseCount(comp.id) : 0}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={statusColors[status] || "bg-muted"}>
                                {statusLabels[status] || status}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild onClick={(e) => e.stopPropagation()}>
                                  <Button variant="ghost" size="icon">
                                    <MoreHorizontal className="h-4 w-4" />
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end">
                                  {!comp ? (
                                    <DropdownMenuItem
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        openEnrollDialog(provider.id);
                                      }}
                                    >
                                      <UserPlus className="h-4 w-4 mr-2" />
                                      Enroll in Compliance
                                    </DropdownMenuItem>
                                  ) : (
                                    <>
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateStatusMutation.mutate({ id: comp.id, status: "active" });
                                        }}
                                      >
                                        Mark Active
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateStatusMutation.mutate({ id: comp.id, status: "pending_verification" });
                                        }}
                                      >
                                        Mark Pending Verification
                                      </DropdownMenuItem>
                                      <DropdownMenuItem
                                        onClick={(e) => {
                                          e.stopPropagation();
                                          updateStatusMutation.mutate({ id: comp.id, status: "suspended" });
                                        }}
                                        className="text-red-600"
                                      >
                                        Suspend
                                      </DropdownMenuItem>
                                    </>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                )}
              </CardContent>
            </Card>
          </div>
        </main>
      </div>

      {/* Enroll Dialog */}
      <Dialog open={enrollDialogOpen} onOpenChange={setEnrollDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Enroll Provider in Compliance Tracking</DialogTitle>
            <DialogDescription>
              Add compliance details for {providers.find((p) => p.id === enrollProviderId)?.display_name || "this provider"}.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>NPI Number</Label>
                <Input
                  value={enrollForm.npi_number}
                  onChange={(e) => setEnrollForm((f) => ({ ...f, npi_number: e.target.value }))}
                  placeholder="10-digit NPI"
                />
              </div>
              <div>
                <Label>Tax ID</Label>
                <Input
                  value={enrollForm.tax_id}
                  onChange={(e) => setEnrollForm((f) => ({ ...f, tax_id: e.target.value }))}
                  placeholder="XX-XXXXXXX"
                />
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Specialty</Label>
                <Input
                  value={enrollForm.specialty}
                  onChange={(e) => setEnrollForm((f) => ({ ...f, specialty: e.target.value }))}
                  placeholder="e.g., Family Medicine"
                />
              </div>
              <div>
                <Label>Primary State</Label>
                <Input
                  value={enrollForm.primary_state}
                  onChange={(e) => setEnrollForm((f) => ({ ...f, primary_state: e.target.value }))}
                  placeholder="e.g., MN"
                  maxLength={2}
                />
              </div>
            </div>
            <div>
              <Label>Notes</Label>
              <Textarea
                value={enrollForm.notes}
                onChange={(e) => setEnrollForm((f) => ({ ...f, notes: e.target.value }))}
                placeholder="Any additional notes..."
              />
            </div>
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setEnrollDialogOpen(false)}>
                Cancel
              </Button>
              <Button
                onClick={() => enrollProviderId && enrollMutation.mutate({ providerId: enrollProviderId, form: enrollForm })}
                disabled={!enrollProviderId || enrollMutation.isPending}
                className="bg-emerald-600 hover:bg-emerald-700"
              >
                Enroll Provider
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {selectedComplianceId && (
        <ProviderDetailSheet
          open={detailOpen}
          onOpenChange={setDetailOpen}
          complianceId={selectedComplianceId}
        />
      )}
    </div>
  );
}
